import type { StateCreator } from 'zustand';
import type { StoreState, GridSettings, MapDrawing, MapRegion, PartyMember, NPC, POI } from '../types/types';
import { Decimal } from 'decimal.js';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

type MarkerType = 'player' | 'npc' | 'poi';
const markerTypeToStateKey: Record<MarkerType, 'partyRoster' | 'npcs' | 'pois'> = {
    player: 'partyRoster',
    npc: 'npcs',
    poi: 'pois',
};

export interface MapState {
    activeMapId: number | null;
    highlightedMarkerId: number | null;
    selectedMarkerId: number | null;
    drawingMode: string | null;
    mapDrawings: MapDrawing[];
    mapRegions: MapRegion[];
}

export interface MapActions {
    setDrawingMode: (mode: string | null) => void;
    setActiveMap: (mapId: number | null) => void;
    updateMarkerPosition: (type: 'player' | 'npc' | 'poi', id: number, x: number, y: number) => void;
    highlightMarker: (markerId: number | null) => void;
    setSelectedMarker: (markerId: number | null) => void;
    updateGridSettings: (mapId: number, settings: GridSettings) => void;
    updateFogOfWar: (mapId: number, fogData: string) => Promise<void>;
    addMapDrawing: (drawing: Omit<MapDrawing, 'id'> & { id?: number }) => Promise<void>;
    updateMapDrawing: (drawing: Partial<MapDrawing> & { id: number }) => Promise<void>;
    deleteMapDrawing: (id: number) => Promise<void>;
    addMapRegion: (region: Omit<MapRegion, 'id'> & { id?: number }) => Promise<void>;
    updateMapRegion: (region: Partial<MapRegion> & { id: number }) => Promise<void>;
    deleteMapRegion: (id: number) => Promise<void>;
}

export type MapSlice = MapState & MapActions;

export const createMapActions: StateCreator<StoreState, [['zustand/immer', never]], [], { state: MapState, actions: MapActions }> = (set, get) => ({
    state: {
        activeMapId: 1, // Default to the ID of the default map from seed.sql
        highlightedMarkerId: null,
        selectedMarkerId: null,
        drawingMode: null,
        mapDrawings: [],
        mapRegions: [],
    },
    actions: {
        setDrawingMode: (mode) => set(store => {
            store.session.map.drawingMode = mode;
        }),
        setActiveMap: async (mapId) => {
            const originalMapId = get().session.map.activeMapId;
            // Optimistically update the UI
            set(store => {
                store.session.map.activeMapId = mapId;
            });
            try {
                await api('/api/user/settings', { method: 'PUT', body: JSON.stringify({ activeMapId: mapId }) });
            } catch (error) {
                logger.error(error, `Failed to set active map to ${mapId}, rolling back.`);
                set(store => { store.session.map.activeMapId = originalMapId; });
            }
        },
        updateMarkerPosition: (type: MarkerType, id: number, x: number, y: number) => set((store: StoreState) => {
            const arrayName = markerTypeToStateKey[type];
            const item = ((store.session.roster[arrayName] as (PartyMember | NPC | POI)[]) ?? []).find(i => i.id === id);
            if (item) { 
                item.posX = x;
                item.posY = y; 
            }
        }),
        highlightMarker: (markerId) => set(store => {
            store.session.map.highlightedMarkerId = markerId;
        }),
        setSelectedMarker: (markerId) => set(store => {
            store.session.map.selectedMarkerId = markerId;
        }),
        updateGridSettings: async (mapId, settings) => {
            const originalGrids = get().settings.grids;
            set(store => {
                store.settings.grids[mapId] = settings;
            });
            try {
                await api(`/api/maps/${mapId}/grid`, { method: 'PUT', body: JSON.stringify(settings) });
            } catch (error) {
                logger.error(error, `Failed to save grid settings for map ${mapId}, rolling back.`);
                set(store => { store.settings.grids = originalGrids; });
            }
        },
        updateFogOfWar: async (mapId, fogData) => {
            // Update local state immediately for a responsive UI
            set(store => {
                store.settings.fogOfWar.data = fogData;
            });
            api(`/api/maps/${mapId}/fog`, { method: 'PUT', body: JSON.stringify({ fogData }) })
                .catch(err => logger.error(err, `Failed to save fog of war for map ${mapId}`));
        },
        addMapDrawing: async (drawing) => {
            const tempId = -Date.now();
            const tempDrawing = { ...drawing, id: tempId };
            set(state => { state.session.map.mapDrawings.push(tempDrawing as MapDrawing); });

            try {
                const response = await api('/api/maps/drawings', { method: 'POST', body: JSON.stringify(drawing) });
                const newDrawing = await response.json() as MapDrawing;
                set(state => {
                    const index = (state.session.map.mapDrawings ?? []).findIndex((d: MapDrawing) => d.id === tempId);
                    if (index !== -1) state.session.map.mapDrawings[index] = newDrawing;
                });
            } catch (error) {
                logger.error(error, 'Error adding map drawing, rolling back.');
                set(state => { state.session.map.mapDrawings = (state.session.map.mapDrawings ?? []).filter((d: MapDrawing) => d.id !== tempId); });
                throw error;
            }
        },
        updateMapDrawing: async (drawing) => {
            const originalDrawings = get().session.map.mapDrawings;
            const drawingIndex = (originalDrawings ?? []).findIndex(d => d.id === drawing.id);
            if (drawingIndex === -1) return;

            set(state => { state.session.map.mapDrawings[drawingIndex] = { ...state.session.map.mapDrawings[drawingIndex]!, ...drawing }; });

            try {
                await api(`/api/maps/drawings/${drawing.id}`, { method: 'PUT', body: JSON.stringify(drawing) });
            } catch (error) {
                logger.error(error, `Error updating map drawing ${drawing.id}, rolling back.`);
                set(state => { state.session.map.mapDrawings = originalDrawings; });
                throw error;
            }
        },
        deleteMapDrawing: async (id) => {
            const originalDrawings = get().session.map.mapDrawings;
            set(state => { state.session.map.mapDrawings = (originalDrawings ?? []).filter(d => d.id !== id); });

            try {
                await api(`/api/maps/drawings/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting map drawing ${id}, rolling back.`);
                set(state => { state.session.map.mapDrawings = originalDrawings; });
                throw error;
            }
        },
        addMapRegion: async (region) => {
            const tempId = -Date.now();
            const tempRegion = { ...region, id: tempId };
            set(state => { state.session.map.mapRegions.push(tempRegion as MapRegion); });

            try {
                const payload = {
                    ...region,
                    opacity: region.opacity ? new Decimal(region.opacity) : null,
                };
                const response = await api('/api/maps/regions', { method: 'POST', body: JSON.stringify(payload) });
                const newRegion = await response.json() as MapRegion;
                set(state => {
                    const index = (state.session.map.mapRegions ?? []).findIndex((r: MapRegion) => r.id === tempId);
                    if (index !== -1) state.session.map.mapRegions[index] = newRegion;
                });
            } catch (error) {
                logger.error(error, 'Error adding map region, rolling back.');
                set(state => { state.session.map.mapRegions = (state.session.map.mapRegions ?? []).filter((r: MapRegion) => r.id !== tempId); });
                throw error;
            }
        },
        updateMapRegion: async (region) => {
            const originalRegions = get().session.map.mapRegions;
            const regionIndex = (originalRegions ?? []).findIndex(r => r.id === region.id);
            if (regionIndex === -1) return;

            set(state => { state.session.map.mapRegions[regionIndex] = { ...state.session.map.mapRegions[regionIndex]!, ...region }; });

            try {
                const payload = {
                    ...region,
                    ...(region.opacity && { opacity: new Decimal(region.opacity) }),
                };
                await api(`/api/maps/regions/${region.id}`, { method: 'PUT', body: JSON.stringify(payload) });
            } catch (error) {
                logger.error(error, `Error updating map region ${region.id}, rolling back.`);
                set(state => { state.session.map.mapRegions = originalRegions; });
                throw error;
            }
        },
        deleteMapRegion: async (id) => {
            const originalRegions = get().session.map.mapRegions;
            set(state => { state.session.map.mapRegions = (originalRegions ?? []).filter(r => r.id !== id); });

            try {
                await api(`/api/maps/regions/${id}`, { method: 'DELETE' });
            } catch (error) {
                logger.error(error, `Error deleting map region ${id}, rolling back.`);
                set(state => { state.session.map.mapRegions = originalRegions; });
                throw error;
            }
        },
    }
});